import BigNumber from 'bignumber.js'
import React, {useState,useEffect,useCallback} from 'react'
import Web3 from 'web3'
import styled from 'styled-components'
import { useWallet } from '@binance-chain/bsc-use-wallet'
import { Button, Heading, Text, Link, Toggle, Flex } from '@pancakeswap-libs/uikit'
import QuestionHelper from 'components/QuestionHelper'
import Input from 'components/Input'
import { useERC20 } from 'hooks/useContract'
import { useAllowance } from 'hooks/useAllowance'
import { useTokenApprove } from 'hooks/useApprove'
import { useTokenBalance } from 'hooks/useTokenBalance'
import { getSep20FactoryAddress } from 'utils/addressHelpers'
import useI18n from 'hooks/useI18n'

import { useFeeToken, useCreateSep20TokenAnnuity } from '../hooks/useSep20Factory'


const Row = styled.div`
  margin-top: 25px;
`;


const Label = styled(Text)`
  text-align: left;
  padding-left: 16px;
  margin-bottom: 5px;
`

const Note = styled(Text)`
    text-align: left;
    padding-left: 16px;
`

const Notification = styled(Text)`
  line-height: 18px;
  padding-top: 10px;
`


interface CreateProps {
  onSuccess?: any
}

const Create: React.FC<CreateProps> = ({ onSuccess }) => {
  const TranslateString = useI18n();
    const { account } = useWallet();

    const [name, setName] = useState('')
    const handleNameChange = (e: React.FormEvent<HTMLInputElement>) => {
      setName(e.currentTarget.value); 
    }
    
    const [symbol, setSymbol] = useState('')
    const handleSymbolChange = (e: React.FormEvent<HTMLInputElement>) => {
      setSymbol(e.currentTarget.value);
    }

    const [initialSupply, setInitialSupply] = useState('')
    const handleInitialSupplyChange = (e: React.FormEvent<HTMLInputElement>) => {
      setInitialSupply(e.currentTarget.value);
    }

    // APY
    const [apy, setApy] = useState("100")
    const handleApyChange = (e: React.FormEvent<HTMLInputElement>) => {
        setApy(e.currentTarget.value);
    }

    // Payout Period
    const [payoutPeriod, setPayoutPeriod] = useState("365")
    const handlePayoutPeriodChange = (e: React.FormEvent<HTMLInputElement>) => {
        setPayoutPeriod(e.currentTarget.value);
    }

    // End Interest Year
    const [endInterestYear, setEndInterestYear] = useState("50")
    const handleEndInterestYearChange = (e: React.FormEvent<HTMLInputElement>) => {
      setEndInterestYear(e.currentTarget.value);
    }

    const feeToken:any = useFeeToken();
    const feeTokenString = `${feeToken.creationFee} ${feeToken.symbol}`;
    const feeTokenBalance = useTokenBalance(feeToken.address).div(new BigNumber(10).pow(feeToken.decimals));
    const notEnoughFee = feeTokenBalance.isLessThan(feeToken.creationFee);

    const [requestedApproval, setRequestedApproval] = useState(false)
    const [approved, setApproved] = useState(0)
    const [pendingTx, setPendingTx] = useState(false)

    const tokenContract = useERC20(feeToken.address);
    const allowance = useAllowance(tokenContract, getSep20FactoryAddress(), approved);
    const onApprove = useTokenApprove(tokenContract, getSep20FactoryAddress())
    const allowed = allowance && !allowance.div(new BigNumber(10).pow(feeToken.decimals)).isLessThan(feeToken.creationFee);

    const handleApprove = useCallback(async () => {
        try {
            setRequestedApproval(true)
            const txHash = await onApprove()
            setRequestedApproval(false)
            if (txHash) {
                setApproved(approved+1);
            }
        } catch (e) {
            console.error(e)
        }
    }, [onApprove, approved])

    const onCreate = useCreateSep20TokenAnnuity();
    const handleSubmit = useCallback(async () => {
        try {
          const txHash = await onCreate(name, symbol, new BigNumber(initialSupply), (Math.ceil(Number(payoutPeriod))*86400).toFixed(0), 
            (Number(endInterestYear) * 31536000 + Date.now() / 1000).toFixed(0), (Number(apy)*100).toFixed(0));
          if (txHash) onSuccess();
          return txHash;
        } catch (e) {
          console.error(e)
        }

        return false;
      }, [onCreate, name, symbol, initialSupply, apy, payoutPeriod, endInterestYear, onSuccess])

    let invalidString = "";
    if (!name) invalidString = TranslateString(1534,"Invalid Token Name");
    else if (name.length > 40) invalidString = TranslateString(1535,"Token Name Too Long");
    else if (!symbol) invalidString = TranslateString(1536,"Invalid Token Symbol");
    else if (symbol.length > 10) invalidString = TranslateString(1537,"Token Symbol Too Long");
    else if (!initialSupply || Number.isNaN(Number(initialSupply)) || Number(initialSupply)<=0 || new BigNumber(initialSupply).isGreaterThan(1e9) ) invalidString = TranslateString(1539,"Invalid Supply");
    else if (!apy || Number.isNaN(Number(apy)) || Number(apy) <= 0 || Number(apy) > 300) invalidString = TranslateString(1544,"Invalid APY");
    else if (!payoutPeriod || Number.isNaN(Number(payoutPeriod)) || Number(payoutPeriod) <= 0) invalidString = TranslateString(1545,"Invalid Payout Period");
    else if (!endInterestYear || Number.isNaN(Number(endInterestYear)) || Number(endInterestYear) <= 0 || Number(endInterestYear) > 50) invalidString = TranslateString(1546,"Invalid End of Interest Year");

    const renderButton = () => {
        if (!allowed && !notEnoughFee) {
            return (
                <Button disabled={requestedApproval} onClick={handleApprove}>
                    {requestedApproval?TranslateString(330,"Approving"):TranslateString(316,"Approve")} {feeToken.symbol}
                </Button>
            )
        }
        return (
            <Button disabled={pendingTx || notEnoughFee || !!invalidString} onClick={async () => {
                setPendingTx(true)
                const txHash = await handleSubmit()
                setPendingTx(false)
              }}>
                {notEnoughFee?TranslateString(319,"Insufficient Fee"):(invalidString||TranslateString(320,"Submit"))}
            </Button>
        )
    }
    return (
        <>
            <Row>
              <Label>{TranslateString(410,'Name:')}</Label>
              <Input placeholder={TranslateString(411,'Token Name')} value={name} onChange={handleNameChange} />
            </Row>
            <Row>
              <Label>{TranslateString(412,'Symbol:')}</Label>
              <Input placeholder={TranslateString(415,"Token Symbol")} value={symbol} onChange={handleSymbolChange} />
            </Row>
            <Row>
              <Label>{TranslateString(1032,'Initial Supply (Max: 1 Billion):')}</Label>
              <Input placeholder={TranslateString(413,'Initial Supply')} value={initialSupply} onChange={handleInitialSupplyChange} />
            </Row>
            <Row>
                <Label><Flex alignItems="center" justifyContent="flex-start">
                    <div>{TranslateString(1033,'Staking APY (Max: 300&#37;):').replace(/&#37;/g,"%")}</div>
                    <QuestionHelper text={TranslateString(1034, 'The effective rate of return for one year. It can have up to two decimal places.')} />
                </Flex></Label>
                <Input placeholder={TranslateString(1039,'APY')} value={apy} onChange={handleApyChange} />
            </Row>
            <Row>
                <Label><Flex alignItems="center" justifyContent="flex-start">
                    <div>{TranslateString(1035,'Payout Period (Days):')}</div>
                    <QuestionHelper text={TranslateString(1036,'In the payout mode, balance is gradually available for a user in this period.')} />
                </Flex></Label>
                <Input placeholder={TranslateString(1040,"Payout Period")} value={payoutPeriod} onChange={handlePayoutPeriodChange} />
            </Row>
            <Row>
                <Label><Flex alignItems="center" justifyContent="flex-start">
                    <div>{TranslateString(1037,'Number of Inflationary Years (Max: 50 Years):')}</div>
                    <QuestionHelper text={TranslateString(1038,'Staking interest will stop after that and the token will become noninflationary.')} />
                </Flex></Label>
                <Input placeholder={TranslateString(1041,"Inflationary Years")} value={endInterestYear} onChange={handleEndInterestYearChange} />
            </Row>
            <Row>
              <Notification>{TranslateString(315,"Fee")}: {feeTokenString}</Notification>
            </Row>
            <Row>
              {renderButton()}
            </Row>
        </>
    )
}

export default Create
 